import type { DrawingContext } from '@studio/design-system/canvas';
import { drawCircle, pushState } from '@studio/design-system/canvas';
import { Origin } from '@studio/js-utils/geometry';
import type { Coordinate } from '@studio/js-utils/geometry';
import * as Constants from './constants.js';
import type { Formula } from './types.js';
import { Pace, Tempo } from '#~/control/movement/index.js';

/* Make the 'medium' linear gradient (yellow->green) */
const makeGradient = (
  context: DrawingContext,
  start: Coordinate,
  end: Coordinate
): CanvasGradient => {
  const { palette, surface } = context;

  const gradient = surface.createLinearGradient(start.x, start.y, end.x, end.y);

  gradient.addColorStop(0, palette.warning.toRgbString());
  gradient.addColorStop(1, palette.success.toRgbString());

  return gradient;
};

/* Draw the arrowhead. */
const drawArrowhead = ({ surface }: DrawingContext, tip: Coordinate): void => {
  const { Arrow } = Constants;

  surface.lineJoin = 'round';
  surface.moveTo(tip.x - Arrow.width, tip.y + Arrow.height / 2);
  surface.lineTo(tip.x, tip.y);
  surface.lineTo(tip.x - Arrow.width, tip.y - Arrow.height / 2);
  surface.closePath();
};

/* Draw the actual joystick. */
const drawJoystick = (context: DrawingContext, formula: Formula): void => {
  const { Arrow } = Constants;
  const { palette, surface } = context;
  const { distance, intent, rate } = formula;

  const pace = rate > 0.8 ? Pace.Fast : rate < 0.2 ? Pace.Slow : Pace.Medium;

  const inSafeZone = isInSafeZone(distance);
  const isZooming = intent.zoom !== 0;

  const tip = {
    x: Math.min(Math.max(Constants.SafeRadius, distance), Constants.Threshold),
    y: 0
  };

  // Compute line position.
  const line = {
    end: {
      // End 1px inside the arrowhead.
      x: tip.x - Arrow.width + 1,
      y: 0
    },
    start: {
      // Start 1px inside the origin circle.
      x: Constants.OriginRadius - 1,
      y: 0
    }
  };

  // Shadow pass
  pushState(context, () => {
    surface.strokeStyle = Constants.ShadowColor;
    surface.lineWidth = 3;

    // Origin
    surface.beginPath();
    drawCircle(surface, Origin, Constants.OriginRadius);
    surface.stroke();

    // No line/arrow when in safe zone and zooming.
    if (isZooming && inSafeZone) {
      return;
    }

    // Arrowhead
    surface.beginPath();
    drawArrowhead(context, tip);
    surface.stroke();

    // Line
    surface.lineWidth = 6.5;

    surface.beginPath();
    surface.moveTo(line.start.x, line.start.y);
    surface.lineTo(line.end.x, line.end.y);
    surface.stroke();
  });

  // Color pass.
  pushState(context, () => {
    // Origin
    surface.fillStyle = isInSafeZone(distance)
      ? isZooming
        ? palette.warning.toRgbString()
        : palette.white.toRgbString()
      : pace === Pace.Fast
        ? palette.success.toRgbString()
        : palette.warning.toRgbString();

    surface.beginPath();
    drawCircle(surface, Origin, Constants.OriginRadius);
    surface.fill();

    // Zoom plus/minus
    if (isZooming) {
      pushState(context, () => {
        surface.rotate(-formula.angle);

        surface.lineWidth = 2;
        surface.strokeStyle = palette.black.toRgbString();
        surface.lineCap = 'round';

        const halfZoom = Constants.ZoomSize / 2;

        surface.beginPath();
        surface.moveTo(Origin.x - halfZoom, Origin.y);
        surface.lineTo(Origin.x + halfZoom, Origin.y);

        if (intent.zoom > 0) {
          surface.moveTo(Origin.x, Origin.y - halfZoom);
          surface.lineTo(Origin.x, Origin.y + halfZoom);
        }

        surface.stroke();
      });
    }

    // No line/arrow when in safe zone and zooming.
    if (isZooming && inSafeZone) {
      return;
    }

    // Arrowhead
    surface.fillStyle = isInSafeZone(distance)
      ? palette.white.toRgbString()
      : pace === Pace.Slow
        ? palette.warning.toRgbString()
        : palette.success.toRgbString();

    surface.beginPath();
    drawArrowhead(context, tip);
    surface.fill();

    // Line
    surface.lineWidth = 4;

    surface.strokeStyle = isInSafeZone(distance)
      ? palette.white.toRgbString()
      : pace === Pace.Slow
        ? palette.warning.toRgbString()
        : pace === Pace.Fast
          ? palette.success.toRgbString()
          : makeGradient(context, line.start, line.end);

    surface.beginPath();
    surface.moveTo(line.start.x, line.start.y);
    surface.lineTo(line.end.x, line.end.y);
    surface.stroke();
  });
};

/* Draw the safe zone */
const drawSafeZone = ({ surface }: DrawingContext, radius: number): void => {
  surface.fillStyle = Constants.SafeColor;

  surface.beginPath();
  drawCircle(surface, Origin, radius);
  surface.fill();
};

/** @internal */
export const drawCanvas = (context: DrawingContext, formula: Formula): void => {
  const { palette, surface } = context;
  const { angle, distance, origin, rate, pointer, speed, tempo } = formula;

  const pace = rate > 0.8 ? Pace.Fast : rate < 0.2 ? Pace.Slow : Pace.Medium;

  pushState(context, () => {
    // Translate and rotate coordinate system
    surface.translate(origin.x, origin.y);
    surface.rotate(angle);

    // Draw the threshold circle when using the `Variable` tempo.
    if (tempo === Tempo.Variable) {
      pushState(context, () => {
        surface.strokeStyle = isInSafeZone(distance)
          ? palette.white.toRgbString()
          : pace === Pace.Slow
            ? palette.warning.toRgbString()
            : pace === Pace.Fast
              ? palette.success.toRgbString()
              : makeGradient(
                  context,
                  { x: -Constants.Threshold, y: 0 },
                  { x: Constants.Threshold, y: 0 }
                );

        surface.setLineDash([4, 4]);

        surface.beginPath();
        drawCircle(surface, Origin, Constants.Threshold);
        surface.stroke();
      });
    }

    // Draw the safe zone.
    pushState(context, drawSafeZone, Constants.SafeRadius);

    // Draw the joystick
    pushState(context, drawJoystick, formula);
  });

  // Draw the pointer (if beyond the threshold)
  if (distance > Constants.Threshold) {
    pushState(context, () => {
      surface.strokeStyle = Constants.ShadowColor;
      surface.lineWidth = 3;

      surface.beginPath();
      drawCircle(surface, pointer, 4);
      surface.stroke();

      surface.strokeStyle = palette.white.toRgbString();
      surface.lineWidth = 1.5;

      surface.beginPath();
      drawCircle(surface, pointer, 4);
      surface.stroke();
    });
  }

  // Draw speed percentage when using the `Variable` tempo.
  if (tempo === Tempo.Variable && speed > 0) {
    pushState(context, () => {
      surface.fillStyle = palette.white.toRgbString();
      surface.strokeStyle = Constants.ShadowColor;
      surface.font = 'bold 18px Iris';
      surface.textAlign = 'center';
      surface.textBaseline = 'middle';

      const label = `${Math.round(speed * 100)}%`;

      const { x } = origin;
      const y = origin.y - 60;

      surface.fillText(label, x, y);
      surface.strokeText(label, x, y);
    });
  }
};

/** @internal */
export const drawCursor = (context: DrawingContext, position?: Coordinate): void => {
  const { palette, surface } = context;

  if (!position) {
    return;
  }

  pushState(context, () => {
    surface.translate(position.x, position.y);

    // Outer ring
    pushState(context, drawSafeZone, Constants.OriginRadius);

    // Inner ring
    pushState(context, () => {
      surface.strokeStyle = Constants.ShadowColor;
      surface.fillStyle = palette.white.toRgbString();
      surface.lineWidth = 1.5;

      surface.beginPath();
      drawCircle(surface, Origin, Constants.OriginRadius * 0.6);
      surface.fill();
      surface.stroke();
    });
  });
};

/**
 * Determines if the pointer is inside the "safe zone".
 *
 * @param distance - Distance of the pointer from the origin.
 * @returns True if the pointer is inside the safe zone.
 */
export function isInSafeZone(distance: number): boolean {
  return distance <= Constants.SafeRadius + Constants.Arrow.width;
}
